import type { ReactNode } from 'react';
import { IconSlidersHorizontal } from '@/components/icons/IconSlidersHorizontal';
import type { VehicleFacetOptions } from './vehicleFacetOptions';
import { toggleMake, toggleValue, type VehicleFilterBounds, type VehicleFilters } from './vehicleFilters';
import { VehicleFilterChips } from './VehicleFilterChips';
import { VehicleMakeFilter } from './VehicleMakeFilter';
import { VehicleModelFilter } from './VehicleModelFilter';
import { VehicleRangeFilters } from './VehicleRangeFilters';

type VehicleFilterPanelProps = {
    filters: VehicleFilters;
    options: VehicleFacetOptions;
    bounds: VehicleFilterBounds;
    onChange: (filters: VehicleFilters) => void;
    onReset: () => void;
    hasActiveFilters: boolean;
    titleId: string;
    /** Above the filter groups — the drawer puts the view switch here. */
    intro?: ReactNode;
    /** Below the head, e.g. the drawer's close button. */
    headAction?: ReactNode;
};

/** Every filter of the listing. The sidebar and the mobile drawer render the same panel. */
export function VehicleFilterPanel({
    filters,
    options,
    bounds,
    onChange,
    onReset,
    hasActiveFilters,
    titleId,
    intro,
    headAction,
}: VehicleFilterPanelProps) {
    return (
        <div className="vehicle-filter">
            <div className="vehicle-filter__head">
                <h2 id={titleId} className="vehicle-filter__title label-md">
                    <IconSlidersHorizontal width={18} height={18} aria-hidden="true" />
                    Filter
                </h2>

                <div className="vehicle-filter__head-actions">
                    {hasActiveFilters ? (
                        <button type="button" className="vehicle-filter__reset label-xs" onClick={onReset}>
                            Zurücksetzen
                        </button>
                    ) : null}
                    {headAction}
                </div>
            </div>

            <div className="vehicle-filter__body">
                {intro}

                <VehicleFilterChips
                    legend="Fahrzeugzustand"
                    options={options.condition}
                    selected={filters.condition}
                    onToggle={(value) => onChange({ ...filters, condition: toggleValue(filters.condition, value) })}
                />

                <VehicleMakeFilter
                    options={options.make}
                    selected={filters.make}
                    onToggle={(make) => onChange(toggleMake(filters, make))}
                />

                <VehicleModelFilter
                    groups={options.modelGroups}
                    selected={filters.model}
                    onToggle={(key) => onChange({ ...filters, model: toggleValue(filters.model, key) })}
                    onClear={() => onChange({ ...filters, model: [] })}
                />

                <VehicleFilterChips
                    legend="Fahrzeugtyp"
                    options={options.bodyType}
                    selected={filters.bodyType}
                    onToggle={(value) => onChange({ ...filters, bodyType: toggleValue(filters.bodyType, value) })}
                />

                <VehicleRangeFilters filters={filters} bounds={bounds} onChange={onChange} />

                <VehicleFilterChips
                    legend="Kraftstoff"
                    options={options.fuel}
                    selected={filters.fuel}
                    onToggle={(value) => onChange({ ...filters, fuel: toggleValue(filters.fuel, value) })}
                />

                <VehicleFilterChips
                    legend="Getriebe"
                    options={options.transmission}
                    selected={filters.transmission}
                    onToggle={(value) =>
                        onChange({ ...filters, transmission: toggleValue(filters.transmission, value) })
                    }
                />
            </div>
        </div>
    );
}
